import { ReactNode, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useGetPartnerDetailAPI } from "@/api/partners/usePartnersAPI";
import {
  HistoryIcon,
  MarkerLookupIcon,
  SearchIcon,
  StorageIcon,
  UploadIcon
} from "@/assets/icons";
import LokasiIntelligenceBvartaLogo from "@/assets/svg/lokasiIntelligenceBvartaLogo.svg?react";
import { BRING_YOUR_OWN_DATA_DATASET_MANAGEMENT_TOUR_ID } from "@/components/onboarding/configs/bring-your-own-data.mocks";
import { WORKSPACE_ANALYSIS_TOUR_ID } from "@/components/onboarding/configs/workspace-tour";
import { Divider } from "@/components/ui/Divider";
import { MESSAGE } from "@/constants/message";
import { useAuth } from "@/hooks/auth/useAuth";
import { useMap } from "@/hooks/map/core/useMapContext";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useHandleClearView from "@/hooks/ui/useHandleClearView";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import { setGeneralSummary } from "@/store/slice/analysisSummary";
import {
  clearCurrRunningAnalysisId,
  clearLatestAnalysisId
} from "@/store/slice/spatialSlice";
import {
  ESidebarMenuType,
  resetUIDataConfigExceptSidebarMenu,
  setAnalysisResultPopup,
  setShowAnalysisSummaryPopup,
  setShowProductOverviewPanel,
  setShowTemplateModal,
  setSideBarMenuTypePopupDisplay,
  toggleDatasetExplorerPopup,
  toggleShowSearchLocationPopup,
  toggleSidebarMenu
} from "@/store/slice/uiSlice";
import { AreaInputType } from "@/types/spatial";
import { withBasePath } from "@/utils/basePath";
import { getSubdomain } from "@/utils/location";

import { twMerge } from "tailwind-merge";

import NavItem from "./navItem";
import SidebarFooter from "./SidebarFooter";
import type { SidebarFooterPopoverType } from "./types";

export type MenuItemType = {
  name: string;
  icon: ReactNode;
  type: string | null;
  onClick: () => void;
  isActive?: boolean;
  hide: boolean;
  // tourId of the onboarding step during which this item renders in its active
  // state. The tour spotlights the whole sidebar via the container's data-tourid
  // (see SIDEBAR_SPOTLIGHT_TOUR_IDS) — this only flags which icon is the anchor.
  spotlightTourId?: string;
};

// Onboarding steps whose spotlight covers the whole sidebar rather than a single
// control. The sidebar container mirrors whichever of these is the active step
// (see `sidebarSpotlightTourId`) so the spotlight hole wraps the entire sidebar,
// while the matching nav item's `spotlightTourId` only flags which icon renders
// in its active state.
const SIDEBAR_SPOTLIGHT_TOUR_IDS: string[] = [
  WORKSPACE_ANALYSIS_TOUR_ID,
  BRING_YOUR_OWN_DATA_DATASET_MANAGEMENT_TOUR_ID
];

const SideBar = () => {
  const { isAuthenticated } = useAuth();
  const userPermissions = useGetUserPermission({ enabled: isAuthenticated });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showConfirmation } = useConfirmationModal();
  const { isTemplateEnabled, isAnalysisHistoryEnabled } = useAppFeatures();
  const {
    handleResetDatasetToHistoryDataset,
    removeAllCatchmentMarkers,
    renderDrawPolygon
  } = useMap();
  const { handleCloseDetailView } = useHandleClearView();

  const [activePopover, setActivePopover] =
    useState<SidebarFooterPopoverType>("");

  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get("preview") === "true";

  const {
    showAnalysisResultPopup,
    showAnalysisSummaryPopup,
    showProductOverviewPanel,
    sidebarMenu,
    showSearchLocationPopup,
    sideBarMenuTypePopupDisplay
  } = useSelector((state: RootState) => state.ui);

  const { spatialConfig } = useSelector((state: RootState) => state.spatial);
  const activeStepTourId = useSelector(
    (state: RootState) => state.onboarding.activeStepTourId
  );
  const sidebarSpotlightTourId =
    activeStepTourId && SIDEBAR_SPOTLIGHT_TOUR_IDS.includes(activeStepTourId)
      ? activeStepTourId
      : WORKSPACE_ANALYSIS_TOUR_ID;
  const { data } = useGetPartnerDetailAPI(getSubdomain());

  const handleClearAIConversation = () => {
    localStorage.removeItem("lokasi_ai_conversations");
  };

  // Currently no requirement for expand the sidebar mas, so keep it empty func
  const toggleSidebar = () => {
    dispatch(toggleSidebarMenu(!sidebarMenu));
  };

  const handleActivePopoverChange = (popoverType: SidebarFooterPopoverType) => {
    setActivePopover(popoverType);
    if (popoverType !== "onboarding") {
      dispatch(setShowProductOverviewPanel(false));
    }
  };

  const footerActivePopover: SidebarFooterPopoverType = showProductOverviewPanel
    ? "onboarding"
    : activePopover;

  const handleChangeTab = (
    callback: () => void,
    type: ESidebarMenuType,
    showConfirmationForSummaryPopup: boolean = true
  ) => {
    const handleConfirm = () => {
      dispatch(resetUIDataConfigExceptSidebarMenu());
      callback();
    };

    const shouldShowConfirmation =
      showAnalysisResultPopup ||
      (showAnalysisSummaryPopup && showConfirmationForSummaryPopup);

    const shouldConfirmLeaveAnalysis =
      showAnalysisSummaryPopup &&
      [
        ESidebarMenuType.DATASET,
        ESidebarMenuType.DATASET_EXPLORER,
        ESidebarMenuType.SEARCH
      ].includes(type);

    if (!shouldShowConfirmation) {
      handleConfirm();
      return;
    }

    showConfirmation({
      title: shouldConfirmLeaveAnalysis
        ? MESSAGE.LEAVE_ANALYSIS_CONFIRMATION_TITLE
        : MESSAGE.UNSAVED_CHANGES_TITLE,
      message: shouldConfirmLeaveAnalysis
        ? MESSAGE.LEAVE_ANALYSIS_CONFIRMATION_DESCRIPTION
        : MESSAGE.CONFIRMATION_LEAVE,
      cancelLabel: "",
      confirmLabel: "Leave",
      onConfirm: () => {
        if (spatialConfig?.areaInput?.value === AreaInputType.POLYGON) {
          renderDrawPolygon();
        }

        if (showAnalysisResultPopup) {
          dispatch(clearLatestAnalysisId());
        }
        toast.dismiss();
        removeAllCatchmentMarkers();
        handleCloseDetailView();
        handleResetDatasetToHistoryDataset();
        handleConfirm();
        handleClearAIConversation();
      }
    });
  };

  const getMenuList = (isPreviewMode: boolean): MenuItemType[] => [
    {
      name: "Analysis",
      type: ESidebarMenuType.SPATIAL,
      icon: <MarkerLookupIcon />,
      isActive: showAnalysisResultPopup,
      spotlightTourId: WORKSPACE_ANALYSIS_TOUR_ID,
      hide: !userPermissions[EModuleName.SPATIAL_ANALYSIS][
        EModulePermission.VIEW
      ],
      onClick: () =>
        handleChangeTab(
          () => {
            if (isPreviewMode) handleResetDatasetToHistoryDataset();
            if (showAnalysisSummaryPopup) {
              dispatch(setGeneralSummary(null));
              dispatch(setShowAnalysisSummaryPopup(false));
              dispatch(setAnalysisResultPopup(true));
            } else {
              navigate("/");
              dispatch(
                setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL)
              );

              if (isTemplateEnabled) {
                const dontShowAgainItem = localStorage.getItem(
                  "template-modal-dont-show-again"
                );
                const dontShowAgain = dontShowAgainItem
                  ? JSON.parse(dontShowAgainItem)
                  : false;

                if (!dontShowAgain) dispatch(setShowTemplateModal(true));
              }
            }
          },
          ESidebarMenuType.SPATIAL,
          false
        )
    },
    {
      name: "Dataset Explorer",
      type: ESidebarMenuType.DATASET_EXPLORER,
      icon: <StorageIcon />,
      hide: !userPermissions[EModuleName.DATASET_EXPLORER][
        EModulePermission.VIEW
      ],
      onClick: () =>
        handleChangeTab(() => {
          if (isPreviewMode) handleResetDatasetToHistoryDataset();
          if (showAnalysisSummaryPopup) {
            dispatch(setGeneralSummary(null));
            dispatch(setShowAnalysisSummaryPopup(false));
            dispatch(setAnalysisResultPopup(false));
          }
          handleClearAIConversation();
          dispatch(toggleDatasetExplorerPopup(true));
          navigate("/");
        }, ESidebarMenuType.DATASET_EXPLORER)
    },
    {
      name: "Dataset Management",
      type: ESidebarMenuType.DATASET,
      icon: <UploadIcon />,
      spotlightTourId: BRING_YOUR_OWN_DATA_DATASET_MANAGEMENT_TOUR_ID,
      hide: !userPermissions[EModuleName.DATASET_MANAGEMENT][
        EModulePermission.VIEW
      ],
      isActive: location.pathname.includes(withBasePath("dataset-management")),
      onClick: () =>
        handleChangeTab(() => {
          if (isPreviewMode) handleResetDatasetToHistoryDataset();
          if (showAnalysisSummaryPopup) {
            dispatch(setGeneralSummary(null));
            dispatch(setShowAnalysisSummaryPopup(false));
            dispatch(setAnalysisResultPopup(false));
          }
          handleClearAIConversation();
          dispatch(toggleDatasetExplorerPopup(false));
          dispatch(clearCurrRunningAnalysisId());
          toast.dismiss();
          navigate("/dataset-management");
        }, ESidebarMenuType.DATASET)
    },
    {
      name: "Search",
      type: ESidebarMenuType.SEARCH,
      icon: <SearchIcon />,
      isActive: showSearchLocationPopup,
      hide: false,
      onClick: () =>
        handleChangeTab(() => {
          if (isPreviewMode) handleResetDatasetToHistoryDataset();
          if (showAnalysisSummaryPopup || showSearchLocationPopup) {
            dispatch(setGeneralSummary(null));
            dispatch(setShowAnalysisSummaryPopup(false));
            dispatch(setAnalysisResultPopup(false));
          }
          handleClearAIConversation();
          dispatch(toggleShowSearchLocationPopup());
          navigate("/");
        }, ESidebarMenuType.SEARCH)
    },
    {
      name: "Historical Analysis",
      type: ESidebarMenuType.ANALYSIS_HISTORY,
      icon: <HistoryIcon />,
      hide: !isAnalysisHistoryEnabled,
      isActive: location.pathname.includes(withBasePath("analysis-history")),
      onClick: () =>
        handleChangeTab(() => {
          if (isPreviewMode) handleResetDatasetToHistoryDataset();
          if (showAnalysisSummaryPopup) {
            dispatch(setGeneralSummary(null));
            dispatch(setShowAnalysisSummaryPopup(false));
            dispatch(setAnalysisResultPopup(false));
          }
          handleClearAIConversation();
          dispatch(toggleDatasetExplorerPopup(false));
          dispatch(clearCurrRunningAnalysisId());
          toast.dismiss();
          navigate("/analysis-history");
        }, ESidebarMenuType.ANALYSIS_HISTORY)
    }
  ];

  const renderLogo = () => {
    const paddingClass = data ? "" : "pl-[20px]";
    const logoClassName = sidebarMenu
      ? `w-full justify-start ${paddingClass}`
      : "w-full justify-center";

    return (
      <Link
        className={twMerge("flex justify-center mb-4", logoClassName)}
        to="/"
      >
        {data?.app_icon_url ? (
          <img
            src={data.app_icon_url}
            className={cn("w-[59px] h-auto")}
            title={data.app_header_title}
            alt={data.app_header_title}
          />
        ) : (
          <LokasiIntelligenceBvartaLogo
            title="LOKASI Intelligence Bvarta Logo"
            className="h-auto w-[23px]"
          />
        )}
      </Link>
    );
  };

  return (
    <section className="flex bg-white">
      <div
        className={twMerge(
          "bg-primary-700 min-h-screen transition-all text-gray-100 flex flex-col border-r border-gainsboro w-16 items-center",
          sidebarMenu ? "w-[264px]" : "w-[60px] items-center"
        )}
        data-tourid={sidebarSpotlightTourId}
      >
        <div className="flex flex-col h-full w-full justify-between">
          <div className="pt-4">
            {renderLogo()}
            <Divider className="bg-white-smoke w-full h-[4px]" />
            <div className="flex flex-col justify-between h-full items-center">
              <div
                className={twMerge(
                  "mt-4 flex flex-col space-y-1.5 gap-3 relative",
                  sidebarMenu ? "flex items-start w-full px-4" : ""
                )}
              >
                {getMenuList(isPreviewMode)
                  .filter((item) => !item.hide)
                  .map((menuItem, i) => (
                    <NavItem
                      menuItem={menuItem}
                      index={i}
                      isExpand={sidebarMenu}
                      isActive={Boolean(
                        (menuItem.type &&
                          menuItem.type === sideBarMenuTypePopupDisplay) ||
                          menuItem.isActive
                      )}
                      isTourHovered={
                        Boolean(menuItem.spotlightTourId) &&
                        menuItem.spotlightTourId === activeStepTourId
                      }
                      key={menuItem.name}
                    />
                  ))}
              </div>
            </div>
          </div>
          <SidebarFooter
            isExpanded={sidebarMenu}
            toggleSidebar={toggleSidebar}
            activePopover={footerActivePopover}
            setActivePopover={handleActivePopoverChange}
          />
        </div>
      </div>
    </section>
  );
};

export default SideBar;
